import {
  Box,
  Button,
  CircularProgress,
  Container,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { InputLabelOutlined } from 'common/components/fields/fields';
import { FORM_RULES } from 'helpers/validations';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Field, reduxForm } from 'redux-form';
import { COMPANY_SIGNED_IN_BACKGROUND_COLOR } from '../../../config/consts';
import {
  acceptInvite,
  resetAcceptState,
  validateInviteToken,
} from '../../../modules/user/pages/homepage/sections/guests/guestsActions';

const PRIMARY_COLOR = '#1976d2';
const PRIMARY_COLOR_HOVER_BG = 'rgba(25, 118, 210, 0.04)';
const SUBMIT_COLOR = '#5759FB';
const SUBMIT_COLOR_HOVER = '#4547dc';
const SUBMIT_COLOR_DISABLED = '#c0c0d8';
const FIELD_FOCUS_COLOR = '#2D95EF';

const useStyles = makeStyles(() => ({
  formScope: {
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: `${FIELD_FOCUS_COLOR} !important`,
    },
    '& label.Mui-focused': {
      color: `${FIELD_FOCUS_COLOR} !important`,
    },
  },
  primaryButton: {
    backgroundColor: `${SUBMIT_COLOR} !important`,
    color: '#ffffff !important',
    borderRadius: '4px !important',
    padding: '10px 32px !important',
    textTransform: 'none !important',
    boxShadow: 'none !important',
    '&:hover': { backgroundColor: `${SUBMIT_COLOR_HOVER} !important` },
    '&:focus': { outline: 'none !important' },
    '&::-moz-focus-inner': { border: 0 },
    '&.Mui-disabled': {
      backgroundColor: `${SUBMIT_COLOR_DISABLED} !important`,
      color: '#ffffff !important',
    },
  },
  outlinedButton: {
    color: `${PRIMARY_COLOR} !important`,
    borderColor: `${PRIMARY_COLOR} !important`,
    borderRadius: '4px !important',
    padding: '10px 24px !important',
    textTransform: 'none !important',
    backgroundColor: '#ffffff !important',
    '&:hover': {
      backgroundColor: `${PRIMARY_COLOR_HOVER_BG} !important`,
      borderColor: `${PRIMARY_COLOR} !important`,
    },
    '&:focus': { outline: 'none !important' },
    '&::-moz-focus-inner': { border: 0 },
  },
}));

const FORM_NAME = 'acceptInviteForm';
const PLATFORM_NAME = 'Sindilegis+';

const ERROR_TITLE = {
  invalid_token: 'Convite inválido',
  already_accepted: 'Convite já aceito',
  revoked: 'Convite revogado',
  expired: 'Convite expirado',
  cpf_already_registered: 'CPF já cadastrado',
  friend_limit_reached_at_accept: 'Sem vagas no momento',
  password_mismatch: 'Senhas não conferem',
  invalid_password: 'Senha inválida',
  inviter_inactive: 'Acesso encerrado',
};

const ERROR_BODY = {
  invalid_token:
    'O link de convite não foi reconhecido. Verifique se você abriu o link correto.',
  already_accepted:
    'Este convite já foi aceito anteriormente. Use seu login normal para entrar.',
  revoked: 'Este convite foi cancelado pelo filiado que te convidou.',
  expired:
    'Este convite expirou. Peça ao filiado que te convidou para enviar um novo.',
  cpf_already_registered:
    'O CPF informado já foi indicado por outra pessoa e está cadastrado',
  friend_limit_reached_at_accept:
    'O filiado que te convidou já atingiu o limite de convidados ativos. Peça para ele liberar uma vaga ou aguarde.',
  inviter_inactive:
    'O acesso não pôde ser concluído porque o filiado que te convidou não está mais ativo.',
};

const passwordsMatch = (value, allValues) =>
  value === allValues.password ? undefined : 'As senhas não conferem.';

function AcceptInvite({
  match,
  location,
  history,
  guests,
  validateInviteToken,
  acceptInvite,
  resetAcceptState,
  handleSubmit,
}) {
  const classes = useStyles();
  const token = match.params.token;
  const channel = new URLSearchParams(location.search || '').get('c');

  useEffect(() => {
    resetAcceptState();
    validateInviteToken(token, channel);
    return () => resetAcceptState();
  }, [token, channel, validateInviteToken, resetAcceptState]);

  const onSubmit = (values) => {
    acceptInvite(token, values.password, values.password_confirmation, history);
  };

  const renderError = (err) => (
    <Box px={4} py={5} textAlign="center">
      <Typography
        variant="body1"
        style={{ fontSize: 22, fontWeight: 'bold', marginBottom: 12 }}
      >
        {ERROR_TITLE[err.code] || 'Não foi possível abrir o convite'}
      </Typography>
      <Typography
        variant="body2"
        color="textSecondary"
        style={{ marginBottom: 24 }}
      >
        {ERROR_BODY[err.code] ||
          err.error ||
          'Tente novamente em alguns minutos.'}
      </Typography>
      <Box display="flex" justifyContent="center">
        <Button
          variant="outlined"
          onClick={() => history.push('/login')}
          className={classes.outlinedButton}
        >
          Ir para o login
        </Button>
      </Box>
    </Box>
  );

  const renderForm = (meta) => (
    <form
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      className={classes.formScope}
    >
      <Box pt={4} pb={2} px={4} textAlign="center">
        <Typography
          variant="body1"
          style={{ fontSize: 24, fontWeight: 'bold' }}
        >
          Bem-vindo(a) ao {PLATFORM_NAME}, {meta.name}!
        </Typography>
        {meta.inviter_name && (
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ marginTop: 8 }}
          >
            Você foi convidado(a) por <strong>{meta.inviter_name}</strong>.
          </Typography>
        )}
      </Box>

      <Box px={4} pb={2}>
        <Typography
          variant="body2"
          style={{ textAlign: 'center', marginBottom: 16 }}
        >
          Crie uma senha para acessar a plataforma.
        </Typography>

        <Box mb={2}>
          <Field
            component={InputLabelOutlined}
            name="password"
            label="Senha (mínimo 6 caracteres)"
            type="password"
            validate={[FORM_RULES.required, FORM_RULES.min(6)]}
            autoComplete="new-password"
          />
        </Box>

        <Box mb={1}>
          <Field
            component={InputLabelOutlined}
            name="password_confirmation"
            label="Confirme a senha"
            type="password"
            validate={[FORM_RULES.required, passwordsMatch]}
            autoComplete="new-password"
          />
        </Box>
      </Box>

      {guests.accept.error && (
        <Box
          mx={4}
          my={2}
          p={2}
          style={{
            backgroundColor: '#fdecea',
            border: '1px solid #f5c2c0',
            borderRadius: 4,
          }}
        >
          <Typography variant="subtitle2" style={{ color: '#a4231a' }}>
            {ERROR_TITLE[guests.accept.error.code] || 'Erro'}
          </Typography>
          <Typography variant="body2" style={{ color: '#a4231a' }}>
            {ERROR_BODY[guests.accept.error.code] ||
              guests.accept.error.error ||
              'Tente novamente.'}
          </Typography>
        </Box>
      )}

      <Box px={4} pt={2} pb={4} display="flex" justifyContent="flex-end" gridGap={12}>
        <Button
          variant="outlined"
          onClick={() => history.push('/login')}
          disabled={guests.accept.loading}
          className={classes.outlinedButton}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={guests.accept.loading}
          className={classes.primaryButton}
          startIcon={
            guests.accept.loading ? (
              <CircularProgress size={16} style={{ color: '#fff' }} />
            ) : null
          }
        >
          {guests.accept.loading ? 'Concluindo...' : 'Concluir cadastro'}
        </Button>
      </Box>
    </form>
  );

  const renderBody = () => {
    if (guests.accept.loading && !guests.accept.meta) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" py={6}>
          <CircularProgress />
        </Box>
      );
    }
    if (guests.accept.error && !guests.accept.meta) {
      return renderError(guests.accept.error);
    }
    if (guests.accept.meta) {
      return renderForm(guests.accept.meta);
    }
    return null;
  };

  return (
    <Box
      minHeight="100vh"
      display="flex"
      alignItems="center"
      style={{
        backgroundColor: COMPANY_SIGNED_IN_BACKGROUND_COLOR || '#fafafa',
      }}
    >
      <Container maxWidth="sm">
        <Box
          style={{
            background: COMPANY_SIGNED_IN_BACKGROUND_COLOR,
            borderRadius: 4,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}
        >
          {renderBody()}
        </Box>
      </Container>
    </Box>
  );
}

const mapStateToProps = (state) => ({ guests: state.guests });
const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    { validateInviteToken, acceptInvite, resetAcceptState },
    dispatch
  );

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(reduxForm({ form: FORM_NAME })(AcceptInvite));
